
import React, { useCallback, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { ImageIcon, Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface ImageUploaderProps {
  onImagesSelect: (files: File[]) => void;
  label: string;
  maxFiles?: number;
  isBatchMode?: boolean;
}

export const ImageUploader = ({ onImagesSelect, label, maxFiles = 9, isBatchMode = false }: ImageUploaderProps) => {
  const [previews, setPreviews] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const filesRef = useRef<File[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const validImageFiles = acceptedFiles.filter(file => file.type.startsWith('image/'));
      if (validImageFiles.length > 0) {
        const selectedFiles = isBatchMode ? validImageFiles : validImageFiles.slice(0, maxFiles);
        filesRef.current = selectedFiles;
        onImagesSelect(selectedFiles);
        
        const newPreviews = selectedFiles.map(file => URL.createObjectURL(file));
        setPreviews(prevPreviews => {
          prevPreviews.forEach(url => URL.revokeObjectURL(url));
          return newPreviews;
        });
        
        toast.success(`${selectedFiles.length} image${selectedFiles.length > 1 ? 's' : ''} uploaded successfully`);
      } else {
        toast.error('Please upload image files');
      }
    }
  }, [onImagesSelect, maxFiles, isBatchMode]);

  const handleRemoveImage = (index: number) => {
    setPreviews(prevPreviews => {
      const newPreviews = [...prevPreviews];
      URL.revokeObjectURL(newPreviews[index]);
      newPreviews.splice(index, 1);
      
      const remainingFiles = [...filesRef.current];
      remainingFiles.splice(index, 1);
      filesRef.current = remainingFiles;
      onImagesSelect(remainingFiles);
      
      return newPreviews;
    });
    toast.success('Image removed successfully');
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.classList.add('opacity-50');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedIndex(null);
    e.currentTarget.classList.remove('opacity-50');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    setPreviews(prevPreviews => {
      const newPreviews = [...prevPreviews];
      const [draggedItem] = newPreviews.splice(draggedIndex, 1);
      newPreviews.splice(dropIndex, 0, draggedItem);

      const newFiles = [...filesRef.current];
      const [draggedFile] = newFiles.splice(draggedIndex, 1);
      newFiles.splice(dropIndex, 0, draggedFile);
      filesRef.current = newFiles;
      onImagesSelect(newFiles);

      return newPreviews;
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif']
    },
    maxFiles: isBatchMode ? undefined : maxFiles
  });

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <p className="text-xl font-bold text-[#6B4EE6]">{label}</p>
      </div>
      <div
        {...getRootProps()}
        className={cn(
          "border-4 border-dashed rounded-[24px] p-8 transition-all duration-300 cursor-pointer",
          "bg-gradient-to-br from-[#E5DEFF] to-[#F2FCE2]/50 backdrop-blur-sm shadow-lg",
          "hover:bg-white hover:shadow-xl",
          "hover:border-[#6B4EE6]/70 hover:scale-[1.01]",
          isDragActive ? "border-[#6B4EE6] bg-[#F2FCE2] scale-[1.02]" : "border-[#9B87F5]",
          "animate-fade-in"
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center gap-4">
          {previews.length > 0 ? (
            <div className="grid grid-cols-3 gap-4">
              {previews.map((preview, index) => (
                <div 
                  key={index} 
                  className="relative group"
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className={cn(
                      "w-24 h-24 object-cover rounded-[20px] shadow-lg",
                      "transform transition-all duration-300",
                      "group-hover:scale-105 group-hover:rotate-2 group-hover:shadow-xl",
                      "cursor-move"
                    )}
                  />
                  <div className="absolute -top-2 -left-2 w-6 h-6 bg-[#6B4EE6] text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md z-10">
                    {index + 1}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(index);
                    }}
                    className="absolute -top-2 -right-2 p-2 bg-white/90 rounded-full
                             shadow-md transform transition-transform duration-300
                             hover:scale-110 hover:bg-red-50 hover:shadow-lg z-10"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                    }}
                  >
                    <X className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-white to-[#F2FCE2] flex items-center justify-center
                            transform transition-all duration-300 hover:scale-110 hover:shadow-lg
                            border-4 border-[#9B87F5] hover:border-[#6B4EE6]/70">
                {isDragActive ? (
                  <Upload className="w-10 h-10 text-[#6B4EE6]" />
                ) : (
                  <ImageIcon className="w-10 h-10 text-[#6B4EE6]" />
                )}
              </div>
              <p className="text-lg text-[#6B4EE6] text-center font-medium">
                {isDragActive ? (
                  "Drop them here!"
                ) : (
                  <>
                    <span className="block font-bold mb-1">Add images to combine into one</span>
                    <span className="text-sm opacity-80">
                      {isBatchMode ? 'Drag & drop unlimited images, or click to browse' : 'Just drag & drop up to 9 images, or click to browse'}
                    </span>
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
